/**
 * The methodology page.
 *
 * A data product that will not explain how it knows things is asking for trust
 * it has not earned. This page is linked from the footer, from every score, and
 * from every empty state — it is load-bearing, not filler.
 */

import { Link } from 'react-router-dom'
import { AssertionBadge } from '@/components/data/trust'
import { ScaleIcon, ShieldCheckIcon, SourceIcon, SpiderIcon } from '@/components/ui/icons'
import { Alert, Panel, SectionHeader } from '@/components/ui/primitives'

export function MethodologyPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
        How this index is built
      </h1>
      <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
        NaijaIndex is a record of what published sources say about Nigerian businesses,
        products and prices. It is not a directory of advertisers, and nothing in it is an
        endorsement. This page sets out where the data comes from, what the scores mean, and
        what the index cannot tell you.
      </p>

      {/* -- Pipeline ------------------------------------------------------- */}
      <section className="mt-9">
        <SectionHeader
          title="Where the data comes from"
          description="Four stages, in order."
        />
        <ol className="mt-4 space-y-4">
          {[
            {
              icon: <SpiderIcon size={16} />,
              title: 'Discovery and crawling',
              body: 'Business websites, marketplaces and public registries are located and read. The crawler honours robots.txt, respects any crawl delay a site requests, caps how much it downloads, and refuses to follow a redirect into a private network.',
            },
            {
              icon: <SourceIcon size={16} />,
              title: 'Extraction',
              body: 'Structured data the business publishes about itself is used first. Page content second, page metadata third. A language model is used only for pages the first three cannot resolve, and its output is validated against a schema before anything is stored.',
            },
            {
              icon: <ScaleIcon size={16} />,
              title: 'Resolution',
              body: 'The same company appears under many names — ABC Ltd, ABC Nigeria Limited, ABC Nig. Those are matched into one record using domain, phone, email and registration number. A name match alone is never enough to merge two businesses.',
            },
            {
              icon: <ShieldCheckIcon size={16} />,
              title: 'Review',
              body: 'Anything the pipeline is not confident about goes to a person before it is published. A reviewer sees the extracted data, the sources, and any record it might duplicate.',
            },
          ].map((stage, index) => (
            <li key={stage.title} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent-subtle)] text-[var(--accent-text)]">
                  {stage.icon}
                </span>
                {index < 3 ? (
                  <span className="mt-1 w-px flex-1 bg-[var(--border-subtle)]" />
                ) : null}
              </div>
              <div className="pb-2">
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                  {stage.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">
                  {stage.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* -- Scores --------------------------------------------------------- */}
      <section id="scores" className="mt-10 scroll-mt-20">
        <SectionHeader
          title="The two scores"
          description="They measure different things and are never combined."
        />

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Panel>
            <p className="field-label" style={{ color: 'var(--color-info)' }}>
              Data quality
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-secondary)]">
              How <strong>complete</strong> the record is. Do we have a phone number, an
              address, products, a description? This is a statement about us, not about the
              business.
            </p>
          </Panel>
          <Panel>
            <p className="field-label" style={{ color: 'var(--accent-text)' }}>
              Corroboration
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-secondary)]">
              How much <strong>independent evidence</strong> supports it. Verified contact
              details, multiple unrelated sources, a claimed profile, a registration number,
              and how recently any of it was checked.
            </p>
          </Panel>
        </div>

        <Alert tone="caution" className="mt-4" title="Why they are shown separately">
          A thoroughly-scraped shell company can score high on completeness and low on
          corroboration. A real manufacturer with a one-page website can be the reverse.
          Averaging the two into a single "rating" would hide exactly the distinction a
          buyer needs.
        </Alert>

        <p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">
          Neither score is a recommendation. A low corroboration score means we have not
          found much evidence — not that a business is untrustworthy. Most Nigerian
          businesses have a small web presence, and the index reflects that rather than
          punishing it.
        </p>
      </section>

      {/* -- Assertion types ------------------------------------------------ */}
      <section className="mt-10">
        <SectionHeader
          title="Facts, inferences and estimates"
          description="Three different kinds of claim, rendered three different ways."
        />
        <div className="mt-4 space-y-3">
          {[
            {
              type: 'fact' as const,
              example: '“Company X lists Product Y at ₦100,000.”',
              body: 'Stated directly by a source we can link to. Follow the link and read it yourself.',
            },
            {
              type: 'inference' as const,
              example: '“Company X appears to be a distributor.”',
              body: 'Derived from evidence — a “find a distributor” page, a pattern of listings — but not stated outright anywhere. Every inference on a profile shows the reasoning behind it.',
            },
            {
              type: 'estimate' as const,
              example: '“Estimated market share: 4%.”',
              body: 'A modelled figure. Indicative only. Never cite one as a measurement.',
            },
          ].map((row) => (
            <Panel key={row.type}>
              <div className="flex flex-wrap items-center gap-2">
                <AssertionBadge type={row.type} />
                <span className="text-sm font-medium text-[var(--text-primary)]">
                  {row.example}
                </span>
              </div>
              <p className="mt-1.5 text-sm text-[var(--text-secondary)]">{row.body}</p>
            </Panel>
          ))}
        </div>
      </section>

      {/* -- Prices --------------------------------------------------------- */}
      <section className="mt-10">
        <SectionHeader title="How prices are handled" />
        <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-[var(--text-secondary)]">
          <li>
            <strong className="text-[var(--text-primary)]">A missing price is never zero.</strong>{' '}
            "Price on request" is recorded as exactly that. Such listings appear in supplier
            tables but are excluded from every average, minimum and trend — including them as
            zero would make every figure on the page wrong.
          </li>
          <li>
            <strong className="text-[var(--text-primary)]">Only like is compared with like.</strong>{' '}
            Averages are computed per currency and per unit. ₦4,500 a bag and ₦95,000 a tonne
            are both cement, and an average across them describes nothing.
          </li>
          <li>
            <strong className="text-[var(--text-primary)]">The original currency is authoritative.</strong>{' '}
            A price observed in naira is stored in naira for ever. Any conversion is done at
            display time and labelled as one.
          </li>
          <li>
            <strong className="text-[var(--text-primary)]">Age is always shown.</strong>{' '}
            Nigerian prices move. Every observation carries the date it was read, and stale
            ones are marked rather than quietly presented as current.
          </li>
          <li>
            <strong className="text-[var(--text-primary)]">Prices are not quotations.</strong>{' '}
            They exclude delivery, taxes and anything negotiated. Confirm with the supplier
            before ordering.
          </li>
        </ul>
      </section>

      {/* -- Limits --------------------------------------------------------- */}
      <section className="mt-10">
        <SectionHeader title="What this index cannot tell you" />
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-[var(--text-secondary)]">
          <li>· Whether a business is solvent, reliable, or will deliver.</li>
          <li>· Whether a price is still valid today.</li>
          <li>· Anything about businesses with no web presence — a large share of Nigerian commerce.</li>
          <li>· Quality, condition or authenticity of goods.</li>
        </ul>
        <p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
          The index is a starting point for finding and comparing suppliers. It is not
          due diligence, and it is not a substitute for talking to the business.
        </p>
      </section>

      {/* -- Corrections ---------------------------------------------------- */}
      <section id="corrections" className="mt-10 scroll-mt-20">
        <Panel>
          <SectionHeader
            title="Corrections and removals"
            description="If a record about your business is wrong, we want to fix it."
          />
          <p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
            Every record shows the sources it was built from. If one of them is wrong, or the
            information has changed, tell us and we will re-check it. Business owners can also
            claim a profile and maintain it directly — a claimed profile keeps the original
            observations alongside the owner's own account, so both remain attributable.
          </p>
          <p className="mt-3 text-sm text-[var(--text-secondary)]">
            Contact:{' '}
            <a href="mailto:corrections@naijaindex.com" className="text-[var(--accent-text)] hover:underline">
              corrections@naijaindex.com
            </a>
          </p>
        </Panel>
      </section>

      <p className="mt-10 text-center text-xs text-[var(--text-tertiary)]">
        <Link to="/" className="hover:underline">Back to search</Link>
      </p>
    </div>
  )
}
